import type { ConversationDTO, ConversationStatus, ThreadType } from '@eriko/shared-types';
import { formatRelativeTime } from '@eriko/shared-types';
import { cn } from '@eriko/ui/src/lib/utils';
import { MessageCircle, Sparkles, User } from 'lucide-react';

const TYPE_ICON: Record<ThreadType, typeof Sparkles> = {
  ERIKO_INIT: Sparkles,
  REVIEWER_INIT: User,
  SME_INIT: MessageCircle,
  REVIEWER_AI: MessageCircle,
};

const STATUS_DOT: Record<ConversationStatus, string> = {
  OPEN: 'bg-warn',
  NEEDS_REPLY: 'bg-neg',
  RESOLVED: 'bg-surface-400',
  ARCHIVED: 'bg-surface-300',
};

interface ConvListItemProps {
  conversation: ConversationDTO;
  isActive: boolean;
  onSelect: (id: string) => void;
}

export function ConvListItem({ conversation, isActive, onSelect }: ConvListItemProps) {
  const Icon = TYPE_ICON[conversation.threadType];

  return (
    <button
      type="button"
      onClick={() => onSelect(conversation.id)}
      className={cn(
        'w-full text-left px-2.5 py-2 rounded-lg mb-px transition-colors',
        isActive ? 'bg-surface-100' : 'hover:bg-surface-50',
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn('w-1.5 h-1.5 rounded-full shrink-0', STATUS_DOT[conversation.status])}
        />
        <Icon className="w-3.5 h-3.5 text-surface-400 shrink-0" strokeWidth={1.8} />
        <span className="flex-1 text-xs font-semibold text-surface-900 truncate">
          {conversation.title || 'New conversation'}
        </span>
        <span className="font-mono text-[0.625rem] text-surface-400 shrink-0">
          {formatRelativeTime(conversation.lastActivityAt)}
        </span>
      </div>
      {conversation.lastMessagePreview && (
        <p className="mt-0.5 pl-5 text-[0.6875rem] leading-snug text-surface-500 truncate">
          {conversation.lastMessageSenderType === 'AI'
            ? ''
            : conversation.lastMessageSenderType === 'REVIEWER'
              ? 'Bookkeeper: '
              : 'Client: '}
          {conversation.lastMessagePreview}
        </p>
      )}
      {conversation.escalatedToReviewerAt && (
        <span className="mt-1 mr-1 inline-block px-1.5 py-0.5 rounded text-[0.5625rem] font-semibold bg-neg-surface text-neg">
          Escalated to reviewer
        </span>
      )}
      {conversation.blocksClose && (
        <span className="mt-1 inline-block px-1.5 py-0.5 rounded text-[0.5625rem] font-semibold bg-warn-surface text-warn">
          Holds month-close
        </span>
      )}
    </button>
  );
}
